/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable react/jsx-sort-props */
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Box, Button, CircularProgress, Typography } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import ResponsiveLayout from '../components/layout/ResponsiveLayout';
import TableHeader from '../components/TableHeader';
import DynamicTable, { TableColumn } from '../components/DynamicTable';
import { DroneProfileService } from '../services/DroneProfileService';
import { DroneProfile, DroneHeartBeat } from '../models/DroneProfile/DroneProfile.model';
import organizationStore from '../stores/OrganizationStore';

const Drones = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedOrg } = organizationStore;

  const [drones, setDrones] = useState<DroneProfile[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [heartbeats, setHeartbeats] = useState<{ [droneId: string]: DroneHeartBeat | null }>({});

  useEffect(() => {
    const fetchDrones = async () => {
      if (!selectedOrg) {
        setError(t('drones.error_no_org'));
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await DroneProfileService.getOrganizationDroneProfiles(selectedOrg);
        const droneList = res.data.drone_profiles || [];
        setDrones(droneList);

        // Fetch heartbeats for each drone
        const heartbeatResults = await Promise.all(
          droneList.map(async (drone) => {
            try {
              const hbRes = await DroneProfileService.getDroneHeartBeat(drone.id);
              if (hbRes && hbRes.success && hbRes.data) {
                return [drone.id, hbRes.data as DroneHeartBeat];
              }
              return [drone.id, null];
            } catch (e) {
              return [drone.id, null];
            }
          })
        );
        setHeartbeats(Object.fromEntries(heartbeatResults));
      } catch (err) {
        setError(t('drones.error_fetch'));
      } finally {
        setLoading(false);
      }
    };
    fetchDrones();
  }, [selectedOrg, t]);

  const ONLINE_WINDOW_MS = 2 * 60 * 1000; // 2 minutes

  // NOTE: 'online' and 'lastHeartbeat' are not real keys of DroneProfile, but we use 'name' as a dummy key for these columns.
  const columns: TableColumn<DroneProfile, keyof DroneProfile>[] = [
    { header: t('drones.table.name') || 'Name', key: 'name' },
    { header: t('drones.table.eid') || 'EID', key: 'eid' },
    {
      header: t('drones.table.model') || 'Model',
      key: 'drone_model_name',
      render: (v: string | Date | undefined, row: DroneProfile) => typeof v === 'string' ? v : '-'
    },
    {
      header: 'Online',
      key: 'name', // dummy key, not used in render
      render: (_: any, row: DroneProfile) => {
        const hb = heartbeats[row.id];
        if (!hb || !hb.timestamp) return <span title="No heartbeat">🔴 Offline</span>;
        const last = new Date(hb.timestamp);
        const now = new Date();
        const online = now.getTime() - last.getTime() < ONLINE_WINDOW_MS;
        return online ? <span title={last.toLocaleString()}>🟢 Online</span> : <span title={last.toLocaleString()}>🔴 Offline</span>;
      }
    },
    {
      header: 'Last Heartbeat',
      key: 'name', // dummy key, not used in render
      render: (_: any, row: DroneProfile) => {
        const hb = heartbeats[row.id];
        if (!hb || !hb.timestamp) return '-';
        return new Date(hb.timestamp).toLocaleString();
      }
    },
    {
      header: t('drones.table.created') || 'Created',
      key: 'createdAt',
      render: (v: string | Date | undefined, row: DroneProfile) => {
        if (v instanceof Date) return v.toLocaleString();
        if (v) return new Date(v).toLocaleString();
        return '-';
      }
    },
    {
      header: t('drones.table.updated') || 'Updated',
      key: 'updatedAt',
      render: (v: string | Date | undefined, row: DroneProfile) => {
        if (v instanceof Date) return v.toLocaleString();
        if (v) return new Date(v).toLocaleString();
        return '-';
      }
    },
  ];

  function renderBody() {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Typography color="danger" level="body-lg" sx={{ textAlign: 'center', mt: 2 }}>{error}</Typography>
      );
    }
    // Add an Actions column to the columns array
    const columnsWithActions: TableColumn<DroneProfile, keyof DroneProfile>[] = [
      ...columns,
      {
        header: t('general_button_labels.actions') || 'Actions',
        key: '' as keyof DroneProfile, // Not a real key, just for actions column
        render: (_: any, row: DroneProfile) => (
          <Button size="sm" color='neutral'  onClick={() => navigate(`/drone-details/${row.id}`)}>
            {t('general_button_labels.view') || 'View'}
          </Button>
        ),
      },
    ];
    return <DynamicTable columns={columnsWithActions} data={drones} selectedRows={[]} />;
  }

  return (
    <>
      <ResponsiveLayout>
        <TableHeader title={t('drones.title') || 'Drones'}>
          <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', gap: 1 }}>
            <Button color='neutral' onClick={() => navigate('/register-drone')} variant='solid'>
              {t('drones.add_drone')}
            </Button>
          </Box>
        </TableHeader>
        <Box sx={{ mt: 2 }}>
          {renderBody()}
        </Box>
      </ResponsiveLayout>
    </>
  );
});

export default Drones;
